// Batch C (AB#2195) — keyboard behaviour shared by the support dialogs: Escape
// closes the TOP dialog only (the cancel dialog can sit over Salon status),
// focus moves into the dialog when it opens and returns to where it was when
// it closes.

import { useEffect, useRef } from 'react';

const openDialogs: number[] = [];
let nextId = 1;

export function useDialogDismiss<T extends HTMLElement>(onDismiss: () => void) {
    const containerRef = useRef<T>(null);
    const dismissRef = useRef(onDismiss);
    dismissRef.current = onDismiss;

    useEffect(() => {
        const id = nextId++;
        openDialogs.push(id);
        const previouslyFocused = document.activeElement as HTMLElement | null;

        const container = containerRef.current;
        if (container && !container.contains(document.activeElement)) {
            const first = container.querySelector<HTMLElement>('textarea, input, select');
            (first ?? container).focus();
        }

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key !== 'Escape' || openDialogs[openDialogs.length - 1] !== id) return;
            e.stopPropagation();
            dismissRef.current();
        };
        document.addEventListener('keydown', onKeyDown);

        return () => {
            document.removeEventListener('keydown', onKeyDown);
            const index = openDialogs.indexOf(id);
            if (index >= 0) openDialogs.splice(index, 1);
            previouslyFocused?.focus?.();
        };
    }, []);

    return containerRef;
}
